import {
  CompleteHarnessOAuthAuthorizationRequest,
  ConnectHarnessCredentialRequest,
  HarnessCredential,
  HarnessCredentialProvider,
  HarnessOAuthAuthorization,
  HarnessOAuthAuthorizationResult,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

/**
 * Model provider credentials for the agent harness. Connecting and revoking are user-scoped only:
 * the org-scope surface is withheld for the initial rollout and the API refuses an org connect.
 * The list still spans both scopes — the user endpoint returns their own credentials plus the
 * organization's, which they inherit for any provider they have not connected themselves, so a
 * member sees what their turns actually spend.
 */

export type ConnectHarnessCredentialVariables =
  ConnectHarnessCredentialRequest & {
    provider: HarnessCredentialProvider;
  };

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ["harnessCredentials"] });
  // Which harnesses are available is derived from these credentials, so the chat selector's
  // answer goes stale the moment one is connected or revoked.
  queryClient.invalidateQueries({ queryKey: ["harnessCapabilities"] });
};

export function useHarnessCredentials(
  options?: Partial<UseQueryOptions<HarnessCredential[], AxiosError>>,
) {
  return useQuery<HarnessCredential[], AxiosError>({
    queryKey: ["harnessCredentials"],
    queryFn: async () => {
      return await orgAPI.harnessCredentials.listUserHarnessCredentials();
    },
    ...options,
  });
}

export function useConnectHarnessCredential(
  options?: UseMutationOptions<
    HarnessCredential,
    AxiosError,
    ConnectHarnessCredentialVariables
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    HarnessCredential,
    AxiosError,
    ConnectHarnessCredentialVariables
  >({
    mutationFn: async ({ provider, ...data }) => {
      return await orgAPI.harnessCredentials.connectUserHarnessCredential(
        provider,
        data,
      );
    },
    onSuccess: () => invalidate(queryClient),
    ...options,
  });
}

export function useRevokeHarnessCredential(
  options?: UseMutationOptions<void, AxiosError, HarnessCredential>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, HarnessCredential>({
    mutationFn: async (credential) => {
      await orgAPI.harnessCredentials.revokeUserHarnessCredential(
        credential.id,
      );
    },
    onSuccess: () => invalidate(queryClient),
    ...options,
  });
}

/**
 * Subscription sign-in. Three hooks because the two providers finish in opposite directions:
 * Anthropic shows the user a code to bring back (complete), ChatGPT takes one from the user and
 * tells us when they are done (poll). Which one applies is on the authorization's `mode`.
 */

export function useCreateHarnessOAuthAuthorization(
  options?: UseMutationOptions<
    HarnessOAuthAuthorization,
    AxiosError,
    HarnessCredentialProvider
  >,
) {
  return useMutation<
    HarnessOAuthAuthorization,
    AxiosError,
    HarnessCredentialProvider
  >({
    mutationFn: async (provider) =>
      await orgAPI.harnessCredentials.createOAuthAuthorization(provider),
    ...options,
  });
}

export function useCompleteHarnessOAuthAuthorization(
  options?: UseMutationOptions<
    HarnessOAuthAuthorizationResult,
    AxiosError,
    { authorizationId: string } & CompleteHarnessOAuthAuthorizationRequest
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    HarnessOAuthAuthorizationResult,
    AxiosError,
    { authorizationId: string } & CompleteHarnessOAuthAuthorizationRequest
  >({
    mutationFn: async ({ authorizationId, ...data }) =>
      await orgAPI.harnessCredentials.completeOAuthAuthorization(
        authorizationId,
        data,
      ),
    onSuccess: () => invalidate(queryClient),
    ...options,
  });
}

/**
 * Polls one device-code sign-in. Each fetch is also the server's poll of the provider, so the
 * interval is the provider's own — polling faster asks it to rate-limit us. Stops as soon as a
 * credential comes back or the authorization stops being pending.
 */
export function useHarnessOAuthAuthorization(
  authorization?: HarnessOAuthAuthorization,
) {
  const queryClient = useQueryClient();
  const intervalMs = (authorization?.poll_interval_seconds || 5) * 1000;

  return useQuery<HarnessOAuthAuthorizationResult, AxiosError>({
    queryKey: ["harnessOAuthAuthorization", authorization?.id],
    enabled: !!authorization && authorization.mode === "device_code",
    retry: false,
    refetchInterval: (query) => {
      const result = query.state.data;
      if (!result) return intervalMs;
      const done =
        !!result.harnessCredential || result.authorization.status !== "pending";
      return done ? false : intervalMs;
    },
    queryFn: async () => {
      const result = await orgAPI.harnessCredentials.getOAuthAuthorization(
        authorization!.id,
      );
      if (result.harnessCredential) invalidate(queryClient);
      return result;
    },
  });
}
